import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/Auth';
import { cancelPendingPaymentRetry } from '../../../auth/_requests';
import { useModal } from '../../../helpers/hooks/modalContext';
import { showToast } from '../../../helpers/utils/showToast';
import {
  clearNuveiPaymentContext,
  loadNuveiPaymentContext,
  getNuveiUserFacingErrorMessage,
} from '../../../helpers/utils/nuveiPaymentUtils';
import { pushStorage } from '../../../helpers/webPush/pushNotificationStorage';

type ReturnStatus = 'success' | 'failure' | 'pending';

interface Props {
  returnStatus: ReturnStatus;
}

/**
 * Handles the browser redirect back from a Nuvei 3DS challenge.
 * Nuvei uses urlDetails.redirectUrl / cancelUrl from openOrder to send the user here.
 */
export const NuveiPaymentReturnPage = ({ returnStatus }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, currentUser } = useAuth();
  const userId = currentUser?.user?.userId;
  const { setModal } = useModal();

  useEffect(() => {
    const context = loadNuveiPaymentContext();
    clearNuveiPaymentContext();

    const params = new URLSearchParams(location.search);

    const rawError =
      params.get('errorDescription') ||
      params.get('reason') ||
      params.get('message') ||
      params.get('gwErrorReason');

    const isApproved = returnStatus === 'success';
    const isDeclined = returnStatus === 'failure';
    const isPending = returnStatus === 'pending';

    if (context?.flow === 'direct-pay') {
      if (isApproved) {
        if (userId != null) {
          pushStorage.setHasMadeFirstPayment(userId);
        }
        if (context.isManualRetry && context.tenantBuildingId) {
          cancelPendingPaymentRetry(auth?.data.token, context.tenantBuildingId).catch((error) =>
            console.error('Failed to cancel pending payment retry', error)
          );
        }
        setModal('payment-status-direct-successfull', {
          tenantBuildingId: context.tenantBuildingId,
          processDate: new Date().toISOString().split('T')[0],
          tenantPaymentOptionId: context.tenantPaymentOptionId,
          pendingIdvAuthHold: context.pendingIdvAuthHold,
          earnedPoints: context.earnedPoints,
        });
        navigate('/pay-rent', { replace: true });
        return;
      }

      if (isDeclined) {
        const userFriendlyMessage = getNuveiUserFacingErrorMessage(rawError, 'direct-pay');
        setModal('payment-status-direct-declined', {
          message: userFriendlyMessage,
          tenantBuildingId: context.tenantBuildingId,
        });
        navigate('/pay-rent', { replace: true });
        return;
      }

      if (isPending) {
        if (userId != null) {
          pushStorage.setHasMadeFirstPayment(userId);
        }
        showToast({
          message: 'Your payment is being processed. You will receive a confirmation shortly.',
          type: 'info',
        });
        navigate('/dashboard', { replace: true });
        return;
      }
    }

    if (context?.flow === 'add-card') {
      if (isApproved) {
        showToast({
          message: 'Card verification completed. Your payment method will appear shortly.',
          type: 'success',
        });
      } else {
        const userFriendlyMessage = getNuveiUserFacingErrorMessage(rawError, 'add-card');
        showToast({
          message: userFriendlyMessage,
          type: 'error',
        });
      }
      if (context.tenantBuildingId) {
        navigate('/pay-rent', {
          replace: true,
          state: {
            buildingId: context.tenantBuildingId,
            initialStep: 1,
            payPlan: context.payPlan === 'direct' ? 'direct' : 'autopay',
            syncPayFlowFromState: true,
          },
        });
        return;
      }
      navigate('/payment-methods', { replace: true });
      return;
    }

    navigate('/dashboard', { replace: true });
  }, [location.search, navigate, returnStatus, setModal]);

  return (
    <div className='flex min-h-[40vh] items-center justify-center'>
      <div className='flex flex-col items-center gap-3'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-[#0055FF] border-t-transparent' />
        <p className='text-[14px] text-[#6B7285]'>Completing payment verification…</p>
      </div>
    </div>
  );
};
